import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../supabaseClient'
import { getThemeColors, THEME_OPTIONS } from '../theme'
import Header from './Header'
import Footer from './Footer'

const TODOS_OPTION = 'Todos'
const SECTOR_OPTIONS = [TODOS_OPTION, 'Banca/Fintech', 'Tecnologia', 'Consultoria', 'Retail', 'Salud', 'Educacion', 'Manufactura', 'Otro']
const SENIORITY_OPTIONS = [TODOS_OPTION, 'Junior', 'Semi-Senior', 'Senior', 'Lead', 'Manager', 'Director', 'VP', 'C-Level']

const PAISES = ['México', 'Colombia', 'Argentina', 'Chile', 'Perú', 'Brasil', 'España', 'Remoto LATAM']

const CIUDADES = {
  'México': [
    'Ciudad de México', 'Aguascalientes', 'Baja California', 'Baja California Sur',
    'Campeche', 'Chiapas', 'Chihuahua', 'Coahuila', 'Colima', 'Durango',
    'Guanajuato', 'Guerrero', 'Hidalgo', 'Jalisco', 'Estado de México',
    'Michoacán', 'Morelos', 'Nayarit', 'Nuevo León', 'Oaxaca',
    'Puebla', 'Querétaro', 'Quintana Roo', 'San Luis Potosí', 'Sinaloa',
    'Sonora', 'Tabasco', 'Tamaulipas', 'Tlaxcala', 'Veracruz',
    'Yucatán', 'Zacatecas', 'Remoto',
  ],
  'Colombia':     ['Bogotá', 'Medellín', 'Cali', 'Barranquilla', 'Remoto'],
  'Argentina':    ['Buenos Aires', 'Córdoba', 'Rosario', 'Mendoza', 'Remoto'],
  'Chile':        ['Santiago', 'Valparaíso', 'Concepción', 'Remoto'],
  'Perú':         ['Lima', 'Arequipa', 'Trujillo', 'Remoto'],
  'Brasil':       ['São Paulo', 'Rio de Janeiro', 'Belo Horizonte', 'Curitiba', 'Remoto'],
  'España':       ['Madrid', 'Barcelona', 'Valencia', 'Sevilla', 'Remoto'],
  'Remoto LATAM': ['Remoto LATAM'],
}

export default function Profile({ session, theme = THEME_OPTIONS.LIGHT }) {
  const C = getThemeColors(theme)
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [savingPassword, setSavingPassword] = useState(false)
  const [success, setSuccess] = useState('')
  const [error, setError] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [perfil, setPerfil] = useState(null)
  const [passwordSuccess, setPasswordSuccess] = useState('')
  const [showUpgrade, setShowUpgrade] = useState(false)
  const [waitlistDone, setWaitlistDone] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deletingAccount, setDeletingAccount] = useState(false)
  const [form, setForm] = useState({
    nombre: '', sector: 'Banca/Fintech', pais: 'México',
    ciudad: 'Ciudad de México', modalidad: 'Híbrido', seniority: 'Senior',
  })
  const [password, setPassword] = useState({ nueva: '', confirmar: '' })

  useEffect(() => {
    const loadData = async () => {
      setLoading(true)
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) { navigate('/login'); return }
      const { data: profile } = await supabase.from('profiles').select('*').eq('id', user.id).single()
      const { data: sp } = await supabase.from('search_profiles')
        .select('sectores, ubicacion, modalidad, seniority, pais')
        .eq('user_id', user.id)
        .single()
      if (profile) {
        setPerfil(profile)
        setForm(f => ({ ...f, nombre: profile.nombre || '' }))
}
      if (sp) setForm(f => ({
        ...f,
        sector: sp.sectores?.length ? sp.sectores[0] : TODOS_OPTION,
        pais: sp.pais || 'México',
        ciudad: sp.ubicacion || f.ciudad,
        modalidad: sp.modalidad || f.modalidad,
        seniority: sp.seniority || TODOS_OPTION,
      }))
      setLoading(false)
    }

    loadData()
  }, [navigate])

  const handlePaisChange = (pais) => {
    setForm(f => ({ ...f, pais, ciudad: (CIUDADES[pais] || [])[0] || '' }))
  }

  const handleSave = async () => {
    if (!form.nombre) { setError('El nombre es requerido.'); return }
    setSaving(true); setError(''); setSuccess('')
    try {
      const { data: { user } } = await supabase.auth.getUser()
      await supabase.from('profiles').update({ nombre: form.nombre }).eq('id', user.id)
      const selectedSectors = form.sector === TODOS_OPTION ? [] : [form.sector]
      const selectedSeniority = form.seniority === TODOS_OPTION ? null : form.seniority
      const { error: spError } = await supabase.from('search_profiles').upsert({
        user_id: user.id, sectores: selectedSectors, pais: form.pais,
        ubicacion: form.ciudad, modalidad: form.modalidad, seniority: selectedSeniority,
        updated_at: new Date().toISOString(),
      }, { onConflict: 'user_id' })
      if (spError) throw spError
      setSuccess('Perfil actualizado correctamente.')
    } catch (err) { setError('Error al guardar. Intenta de nuevo.') }
    setSaving(false)
  }

  const handlePasswordChange = async () => {
    if (!password.nueva || password.nueva.length < 8) { setPasswordError('La contraseña debe tener al menos 8 caracteres.'); return }
    if (password.nueva !== password.confirmar) { setPasswordError('Las contraseñas no coinciden.'); return }
    setSavingPassword(true); setPasswordError(''); setPasswordSuccess('')
    try {
      const { error } = await supabase.auth.updateUser({ password: password.nueva })
      if (error) throw error
      setPasswordSuccess('Contraseña actualizada correctamente.')
      setPassword({ nueva: '', confirmar: '' })
    } catch (err) { setPasswordError('Error al cambiar la contraseña.') }
    setSavingPassword(false)
  }

  const handleWaitlist = async () => {
    const { data: { user } } = await supabase.auth.getUser()
    await supabase.from('waitlist').upsert({ email: user.email, user_id: user.id }, { onConflict: 'email' })
    setWaitlistDone(true)
  }

  const handleDeleteAccount = async () => {
    setDeletingAccount(true)
    setError('')
    try {
      const { data: { session: activeSession } } = await supabase.auth.getSession()
      const accessToken = activeSession?.access_token
      if (!accessToken) throw new Error('No se encontró una sesión válida.')

      const { error: functionError } = await supabase.functions.invoke('delete-account', {
        headers: { Authorization: `Bearer ${accessToken}` },
      })
      if (functionError) throw functionError

      await supabase.auth.signOut()
      navigate('/')
    } catch (err) {
      console.error(err)
      setError('No pudimos eliminar tu cuenta en este momento. Intenta de nuevo.')
      setDeletingAccount(false)
      setShowDeleteModal(false)
    }
  }

  const inputStyle = { width: '100%', padding: '10px 14px', background: theme === THEME_OPTIONS.DARK ? '#0f172a' : C.bg, border: `0.5px solid ${C.border}`, borderRadius: '8px', color: C.text, fontSize: '14px', outline: 'none', boxSizing: 'border-box' }
  const selectStyle = { ...inputStyle, background: C.white }
  const labelStyle = { display: 'block', fontSize: '12px', color: C.muted, marginBottom: '6px', marginTop: '14px' }
  const sectionStyle = { background: C.white, border: `0.5px solid ${C.border}`, borderRadius: '12px', padding: '24px', marginBottom: '16px' }

  if (loading) return (
    <div style={{ minHeight: '100vh', background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'DM Sans', system-ui, sans-serif" }}>
      <div style={{ color: C.muted, fontSize: '14px' }}>Cargando perfil...</div>
    </div>
  )

  return (
    <div style={{ minHeight: '100vh', background: C.bg, fontFamily: "'DM Sans', system-ui, sans-serif", color: C.text }}>

      {showUpgrade && (
        <div onClick={() => { setShowUpgrade(false); setWaitlistDone(false) }} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: C.white, borderRadius: '16px', padding: '2rem', width: '100%', maxWidth: '420px', boxSizing: 'border-box' }}>
            <div style={{ fontSize: '20px', fontWeight: '500', color: C.text, marginBottom: '4px' }}>Plan Premium</div>
            <div style={{ fontSize: '13px', color: C.muted, marginBottom: '20px' }}>Próximamente. Únete a la lista para ser el primero en acceder.</div>
            {[['Keywords ilimitadas', '3 en plan gratuito'], ['Vacantes ilimitadas', '5 por día en plan gratuito'], ['Alertas Telegram', 'Solo en Premium'], ['Scoring completo detallado', 'Básico en plan gratuito'], ['Pipeline kanban', 'Solo en Premium']].map(([feat, note]) => (
              <div key={feat} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: `0.5px solid ${C.border}`, fontSize: '13px' }}>
                <span style={{ color: C.text, fontWeight: '500' }}>✓ {feat}</span>
                <span style={{ color: C.subtle }}>{note}</span>
              </div>
            ))}
            <div style={{ fontSize: '28px', fontWeight: '500', color: C.blue, margin: '20px 0 16px' }}>$14.99<span style={{ fontSize: '14px', color: C.subtle, fontWeight: '400' }}>/mes</span></div>
            {!waitlistDone ? (
              <button onClick={handleWaitlist} style={{ width: '100%', padding: '12px', background: C.blue, color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer', marginBottom: '8px' }}>Unirme a la lista →</button>
            ) : (
              <div style={{ background: C.greenBg, border: `0.5px solid ${C.greenBorder}`, borderRadius: '8px', padding: '14px', textAlign: 'center', marginBottom: '8px' }}>
                <div style={{ fontSize: '1.5rem', marginBottom: '4px' }}>🎉</div>
                <div style={{ fontSize: '14px', fontWeight: '500', color: C.green }}>¡Ya estás en la lista!</div>
                <div style={{ fontSize: '12px', color: C.muted, marginTop: '4px' }}>Te avisamos cuando lancemos Premium.</div>
              </div>
            )}
            <button onClick={() => { setShowUpgrade(false); setWaitlistDone(false) }} style={{ width: '100%', padding: '10px', background: 'none', border: 'none', color: C.subtle, fontSize: '13px', cursor: 'pointer' }}>Cerrar</button>
          </div>
        </div>
      )}

      {showDeleteModal && (
        <div onClick={() => setShowDeleteModal(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: C.white, borderRadius: '16px', padding: '2rem', width: '100%', maxWidth: '380px', boxSizing: 'border-box' }}>
            <div style={{ fontSize: '18px', fontWeight: '500', color: C.text, marginBottom: '8px' }}>¿Eliminar cuenta?</div>
            <div style={{ fontSize: '13px', color: C.muted, marginBottom: '20px', lineHeight: '1.6' }}>Esta acción es irreversible. Se eliminarán todos tus datos, vacantes y configuración.</div>
            <button onClick={handleDeleteAccount} disabled={deletingAccount} style={{ width: '100%', padding: '12px', background: C.red, color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer', marginBottom: '8px' }}>
              {deletingAccount ? 'Eliminando...' : 'Sí, eliminar mi cuenta'}
            </button>
            <button onClick={() => setShowDeleteModal(false)} style={{ width: '100%', padding: '10px', background: 'none', border: 'none', color: C.subtle, fontSize: '13px', cursor: 'pointer' }}>Cancelar</button>
          </div>
        </div>
      )}

      <Header theme={theme} session={session} perfil={perfil} />

      <div style={{ maxWidth: '640px', margin: '0 auto', padding: 'clamp(16px, 3vw, 32px)', boxSizing: 'border-box' }}>
        <div style={{ marginBottom: '28px' }}>
          <div style={{ fontSize: '22px', fontWeight: '500', color: C.text, letterSpacing: '-0.5px', marginBottom: '4px' }}>Mi perfil</div>
          <div style={{ fontSize: '13px', color: C.subtle }}>{session?.user?.email}</div>
        </div>

        <div style={sectionStyle}>
          <div style={{ fontSize: '15px', fontWeight: '500', color: C.text, marginBottom: '4px' }}>Información personal</div>
          <div style={{ fontSize: '12px', color: C.subtle, marginBottom: '4px' }}>Tu nombre y preferencias de búsqueda</div>
          <label style={labelStyle}>Nombre completo</label>
          <input style={inputStyle} placeholder="Tu nombre" value={form.nombre} onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))} />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px' }}>
            <div>
              <label style={labelStyle}>Sector</label>
              <select style={selectStyle} value={form.sector} onChange={e => setForm(f => ({ ...f, sector: e.target.value }))}>
                {SECTOR_OPTIONS.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Seniority</label>
              <select style={selectStyle} value={form.seniority} onChange={e => setForm(f => ({ ...f, seniority: e.target.value }))}>
                {SENIORITY_OPTIONS.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px' }}>
            <div>
              <label style={labelStyle}>País</label>
              <select style={selectStyle} value={form.pais} onChange={e => handlePaisChange(e.target.value)}>
                {PAISES.map(p => <option key={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Ciudad</label>
              <select style={selectStyle} value={form.ciudad} onChange={e => setForm(f => ({ ...f, ciudad: e.target.value }))}>
                {(CIUDADES[form.pais] || []).map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <label style={labelStyle}>Modalidad</label>
          <select style={selectStyle} value={form.modalidad} onChange={e => setForm(f => ({ ...f, modalidad: e.target.value }))}>
            {['Remoto', 'Híbrido', 'Presencial'].map(m => <option key={m}>{m}</option>)}
          </select>
        </div>

        {error && <div style={{ background: C.redBg, border: `0.5px solid #FECACA`, color: C.red, padding: '10px 14px', borderRadius: '8px', fontSize: '13px', marginBottom: '12px' }}>{error}</div>}
        {success && <div style={{ background: C.greenBg, border: `0.5px solid ${C.greenBorder}`, color: C.green, padding: '10px 14px', borderRadius: '8px', fontSize: '13px', marginBottom: '12px' }}>{success}</div>}

        <div style={{ display: 'flex', gap: '10px', marginBottom: '24px' }}>
          <button onClick={() => navigate('/dashboard')} style={{ flex: 1, padding: '12px', background: C.white, color: C.muted, border: `0.5px solid ${C.border}`, borderRadius: '8px', cursor: 'pointer', fontSize: '14px' }}>← Dashboard</button>
          <button onClick={handleSave} disabled={saving} style={{ flex: 2, padding: '12px', background: saving ? C.bg : C.blue, color: saving ? C.subtle : '#fff', border: 'none', borderRadius: '8px', cursor: saving ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: '500' }}>
            {saving ? 'Guardando...' : 'Guardar cambios'}
          </button>
        </div>

        <div style={sectionStyle}>
          <div style={{ fontSize: '15px', fontWeight: '500', color: C.text, marginBottom: '4px' }}>Seguridad</div>
          <div style={{ fontSize: '12px', color: C.subtle, marginBottom: '4px' }}>Cambia tu contraseña de acceso</div>
          <label style={labelStyle}>Nueva contraseña</label>
          <input style={inputStyle} type="password" placeholder="Mínimo 8 caracteres" value={password.nueva} onChange={e => setPassword(p => ({ ...p, nueva: e.target.value }))} />
          <label style={labelStyle}>Confirmar contraseña</label>
          <input style={inputStyle} type="password" placeholder="Repite la contraseña" value={password.confirmar} onChange={e => setPassword(p => ({ ...p, confirmar: e.target.value }))} />
          {passwordError && <div style={{ background: C.redBg, border: `0.5px solid #FECACA`, color: C.red, padding: '10px 14px', borderRadius: '8px', fontSize: '13px', margin: '12px 0' }}>{passwordError}</div>}
          {passwordSuccess && <div style={{ background: C.greenBg, border: `0.5px solid ${C.greenBorder}`, color: C.green, padding: '10px 14px', borderRadius: '8px', fontSize: '13px', margin: '12px 0' }}>{passwordSuccess}</div>}
          <button onClick={handlePasswordChange} disabled={savingPassword} style={{ width: '100%', marginTop: '14px', padding: '11px', background: C.white, color: C.blue, border: `0.5px solid ${C.blue}`, borderRadius: '8px', fontSize: '13px', fontWeight: '500', cursor: 'pointer' }}>
            {savingPassword ? 'Cambiando...' : 'Cambiar contraseña'}
          </button>
        </div>

        <div style={{ ...sectionStyle, borderColor: '#FECACA' }}>
          <div style={{ fontSize: '15px', fontWeight: '500', color: C.red, marginBottom: '4px' }}>Zona de peligro</div>
          <div style={{ fontSize: '12px', color: C.subtle, marginBottom: '16px' }}>Acciones irreversibles sobre tu cuenta.</div>
          <button onClick={() => setShowDeleteModal(true)} style={{ padding: '10px 20px', background: C.white, color: C.red, border: `0.5px solid #FECACA`, borderRadius: '8px', fontSize: '13px', cursor: 'pointer' }}>
            Eliminar mi cuenta
          </button>
        </div>

        <Footer theme={theme} />
      </div>
    </div>
  )
}


